import AddIcon from "@mui/icons-material/Add";
import { Box, Button, Dialog } from "@mui/material";
import { Theme, useTheme } from "@mui/material/styles";
import ConfirmDialog from "components/ConfirmDialog";
import DeleteIconButton from "components/IconButtons/DeleteIconButton";
import EditIconButton from "components/IconButtons/EditIconButton";
import LazyTable, { TableCellType } from "components/LazyTable";
import SnackbarMessage from "components/SnackbarMessage";
import { Severity, SnackbarMessageProps } from "types/common-types";
import { withErrorBoundary } from "hocs/withErrorBoundary";
import useGlobalContext from "hooks/globalData";
import useBoolean from "hooks/useBoolean";
import { useStrictQuery } from "hooks/useStrictQuery";
import { useState } from "react";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQueryClient } from "react-query";
import {
  ContractListRequest,
  IContract,
  ListResponseOfContract,
  OrganizationDetails,
} from "services/KnowfullyAdminClient";
import OrganizationContractForm from "./OrganizationContractForm";

type OrganizationContractsProps = {
  organization: OrganizationDetails;
};

const useStyles = (theme: Theme) => ({
  addButton: {
    display: "flex",
    justifyContent: "end",
    width: "100%",
    margin: theme.spacing(4, 0, 3, 0),
  },
  root: {
    width: "100%",
  },
  buttons: {
    display: "flex",
  },
});

const OrganizationContracts = (props: OrganizationContractsProps) => {
  const { organization } = props;
  const { formatMessage: t } = useIntl();
  const styles = useStyles(useTheme());
  const { contractService } = useGlobalContext();
  const [updateOpen, updateOpenActions] = useBoolean(false);
  const [deleteOpen, deleteOpenActions] = useBoolean(false);
  const [
    contractToUpdate,
    contractToUpdateActions,
  ] = useState<IContract | null>(null);
  const [contractIdToDelete, contractIdToDeleteActions] = useState<
    string | null
  >(null);
  const queryClient = useQueryClient();
  const [
    snackbarMessage,
    setSnackbarMessage,
  ] = useState<SnackbarMessageProps | null>(null);

  const contractsData = useStrictQuery<ListResponseOfContract>(
    ["contractService.getContracts", organization.id],
    () =>
      contractService.getContracts(
        ContractListRequest.fromJS({
          pageSize: -1,
          organizationId: organization.id,
        })
      ),
    {
      keepPreviousData: true,
      enabled: !!organization.id,
    }
  );

  let contracts = contractsData.data?.data ?? [];

  const onSaved = () => {
    if (contractToUpdate) {
      setSnackbarMessage(
        new SnackbarMessageProps(
          t({ id: "contractActionTitle" }, { action: "updated" }),
          Severity.Success
        )
      );
    } else {
      setSnackbarMessage(
        new SnackbarMessageProps(
          t({ id: "contractActionTitle" }, { action: "created" }),
          Severity.Success
        )
      );
    }

    updateOpenActions.setFalse();
    contractToUpdateActions(null);

    queryClient.invalidateQueries([
      "contractService.getContracts",
      organization.id,
    ]);
  };

  const onCancel = () => {
    updateOpenActions.setFalse();
    contractToUpdateActions(null);
  };

  const onDelete = async () => {
    await contractService.deleteContract(contractIdToDelete!);

    queryClient.invalidateQueries([
      "contractService.getContracts",
      organization.id,
    ]);

    setSnackbarMessage(
      new SnackbarMessageProps(
        t({ id: "contractActionTitle" }, { action: "deleted" }),
        Severity.Success
      )
    );

    deleteOpenActions.setFalse();
  };

  const Buttons = ({ value }: { value: IContract }) => {
    return (
      <Box sx={styles.buttons}>
        <EditIconButton
          onClick={(e) => {
            e.stopPropagation();
            contractToUpdateActions(value);
            updateOpenActions.setTrue();
          }}
        />
        <DeleteIconButton
          onClick={(e) => {
            e.stopPropagation();
            deleteOpenActions.setTrue();
            contractIdToDeleteActions(value.id);
          }}
        />
      </Box>
    );
  };

  const cells: Array<TableCellType> = [
    {
      accessor: "contractTypeName",
      align: "left",
      type: "text",
      label: t({ id: "contractType" }),
    },
    {
      accessor: "renewalDate",
      align: "left",
      type: "date",
      label: t({ id: "renewalDate" }),
    },
    {
      accessor: "contractStatusName",
      align: "left",
      type: "text",
      label: t({ id: "statusLabel" }),
    },
    {
      accessor: "value",
      align: "center",
      width: 1,
      type: "node",
      View: Buttons,
      label: "",
      noSort: true,
    },
  ];

  return (
    <Box sx={styles.root}>
      <LazyTable
        {...{
          rowCount: contractsData.data?.totalRecords ?? 0,
          rows: contracts,
          loading: contractsData.isFetching,
          titleComponent: (
            <Box sx={styles.addButton}>
              <Button
                onClick={updateOpenActions.setTrue}
                startIcon={<AddIcon />}
              >
                <T id="addContract" />
              </Button>
            </Box>
          ),
          dense: true,
          cells,
          page: 0,
          order: "asc",
          orderBy: "sequence",
          rowsPerPage: contracts.length,
          setPage: () => {},
          setOrder: () => {},
          setOrderBy: () => {},
          setRowsPerPage: () => {},
          allowPagination: false,
        }}
      />

      <Dialog open={updateOpen} maxWidth="sm" fullWidth disableEnforceFocus>
        <OrganizationContractForm
          onContractSaved={onSaved}
          onCancel={onCancel}
          organization={organization}
          contract={contractToUpdate ?? undefined}
          submitLabel={
            contractToUpdate != null
              ? t({ id: "updateContractButton" })
              : t({ id: "addContractButton" })
          }
        />
      </Dialog>
      <ConfirmDialog
        dialogTitleKey="deleteContractTitle"
        contentKey="deleteContractQuestion"
        open={deleteOpen}
        onCancel={deleteOpenActions.setFalse}
        handleConfirm={onDelete}
      />
      <SnackbarMessage
        snackbarMessage={snackbarMessage}
        setSnackbarMessage={setSnackbarMessage}
      />
    </Box>
  );
};

export default withErrorBoundary(OrganizationContracts);
